import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import {
  demoCustomers,
  demoEmployees,
  demoTasks,
  demoBookings,
  demoInvoices,
  demoEstimates,
  demoProducts,
  demoServices,
  demoExpenses,
  demoSuppliers,
  demoStats,
  demoBusinessProfile
} from "../data/demoData";

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Demo data state (allows modification in sandbox)
  const [customers, setCustomers] = useState(demoCustomers);
  const [employees] = useState(demoEmployees);
  const [tasks, setTasks] = useState(demoTasks);
  const [bookings, setBookings] = useState(demoBookings);
  const [invoices, setInvoices] = useState(demoInvoices);
  const [estimates, setEstimates] = useState(demoEstimates);
  const [products] = useState(demoProducts);
  const [services] = useState(demoServices);
  const [expenses, setExpenses] = useState(demoExpenses);
  const [suppliers] = useState(demoSuppliers);
  const [businessProfile] = useState(demoBusinessProfile);

  // Computed stats based on current state
  const stats = useMemo(() => {
    const paidInvoices = invoices.filter(inv => inv.status === "Paid");
    const unpaidInvoices = invoices.filter(inv => inv.status === "Unpaid");
    const completedTasks = tasks.filter(t => t.status === "completed");
    const pendingTasks = tasks.filter(t => t.status !== "completed");

    return {
      totalRevenue: paidInvoices.reduce((sum, inv) => sum + inv.total, 0),
      pendingInvoices: unpaidInvoices.reduce((sum, inv) => sum + inv.total, 0),
      bookingsThisWeek: bookings.length,
      activeCustomers: customers.length,
      tasksCompleted: completedTasks.length,
      tasksPending: pendingTasks.length,
      employeesActive: employees.length
    };
  }, [invoices, tasks, bookings, customers, employees]);

  // Enter demo mode
  const enterDemoMode = useCallback(() => {
    setIsDemoMode(true);
  }, []);

  // Exit demo mode and reset data
  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false);
    // Reset all data to original
    setCustomers(demoCustomers);
    setTasks(demoTasks);
    setBookings(demoBookings);
    setInvoices(demoInvoices);
    setEstimates(demoEstimates);
    setExpenses(demoExpenses);
  }, []);

  // Reset demo data without exiting
  const resetDemoData = useCallback(() => {
    setCustomers(demoCustomers);
    setTasks(demoTasks);
    setBookings(demoBookings);
    setInvoices(demoInvoices);
    setEstimates(demoEstimates);
    setExpenses(demoExpenses);
  }, []);

  // Task CRUD operations
  const addTask = useCallback((task) => {
    const newTask = {
      id: `demo-task-${Date.now()}`,
      ...task,
      createdAt: new Date().toISOString()
    };
    setTasks(prev => [newTask, ...prev]);
    return newTask;
  }, []);

  const updateTask = useCallback((taskId, updates) => {
    setTasks(prev =>
      prev.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      )
    );
  }, []);

  const deleteTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  }, []);

  // Booking CRUD operations
  const addBooking = useCallback((booking) => {
    const newBooking = {
      id: `demo-booking-${Date.now()}`,
      bookingNumber: `BK-${Date.now()}`,
      ...booking,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setBookings(prev => [newBooking, ...prev]);
    return newBooking;
  }, []);

  const updateBooking = useCallback((bookingId, updates) => {
    setBookings(prev =>
      prev.map(booking =>
        booking.id === bookingId
          ? { ...booking, ...updates, updatedAt: new Date().toISOString() }
          : booking
      )
    );
  }, []);

  const deleteBooking = useCallback((bookingId) => {
    setBookings(prev => prev.filter(booking => booking.id !== bookingId));
  }, []);

  // Invoice CRUD operations
  const addInvoice = useCallback((invoice) => {
    const newInvoice = {
      id: `demo-invoice-${Date.now()}`,
      invoiceNumber: `INV-${Date.now()}`,
      ...invoice,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setInvoices(prev => [newInvoice, ...prev]);
    return newInvoice;
  }, []);

  const updateInvoice = useCallback((invoiceId, updates) => {
    setInvoices(prev =>
      prev.map(invoice =>
        invoice.id === invoiceId
          ? { ...invoice, ...updates, updatedAt: new Date().toISOString() }
          : invoice
      )
    );
  }, []);

  const deleteInvoice = useCallback((invoiceId) => {
    setInvoices(prev => prev.filter(invoice => invoice.id !== invoiceId));
  }, []);

  // Estimate CRUD operations
  const addEstimate = useCallback((estimate) => {
    const newEstimate = {
      id: `demo-estimate-${Date.now()}`,
      estimateNumber: `EST-${Date.now()}`,
      ...estimate,
      createdAt: new Date().toISOString()
    };
    setEstimates(prev => [newEstimate, ...prev]);
    return newEstimate;
  }, []);

  const updateEstimate = useCallback((estimateId, updates) => {
    setEstimates(prev =>
      prev.map(estimate =>
        estimate.id === estimateId ? { ...estimate, ...updates } : estimate
      )
    );
  }, []);

  const deleteEstimate = useCallback((estimateId) => {
    setEstimates(prev => prev.filter(estimate => estimate.id !== estimateId));
  }, []);

  // Customer CRUD operations
  const addCustomer = useCallback((customer) => {
    const newCustomer = {
      id: `demo-customer-${Date.now()}`,
      ...customer,
      createdAt: new Date().toISOString()
    };
    setCustomers(prev => [newCustomer, ...prev]);
    return newCustomer;
  }, []);

  const updateCustomer = useCallback((customerId, updates) => {
    setCustomers(prev =>
      prev.map(customer =>
        customer.id === customerId ? { ...customer, ...updates } : customer
      )
    );
  }, []);

  const deleteCustomer = useCallback((customerId) => {
    setCustomers(prev => prev.filter(customer => customer.id !== customerId));
  }, []);

  // Expense operations
  const addExpense = useCallback((expense) => {
    const newExpense = {
      id: `demo-expense-${Date.now()}`,
      ...expense,
      date: new Date().toISOString()
    };
    setExpenses(prev => [newExpense, ...prev]);
    return newExpense;
  }, []);

  const value = useMemo(() => ({
    // State
    isDemoMode,

    // Data
    customers,
    employees,
    tasks,
    bookings,
    invoices,
    estimates,
    products,
    services,
    expenses,
    suppliers,
    stats,
    businessProfile,

    // Mode control
    enterDemoMode,
    exitDemoMode,
    resetDemoData,

    // Task operations
    addTask,
    updateTask,
    deleteTask,

    // Booking operations
    addBooking,
    updateBooking,
    deleteBooking,

    // Invoice operations
    addInvoice,
    updateInvoice,
    deleteInvoice,

    // Estimate operations
    addEstimate,
    updateEstimate,
    deleteEstimate,

    // Customer operations
    addCustomer,
    updateCustomer,
    deleteCustomer,

    // Expense operations
    addExpense
  }), [
    isDemoMode,
    customers,
    employees,
    tasks,
    bookings,
    invoices,
    estimates,
    products,
    services,
    expenses,
    suppliers,
    stats,
    businessProfile,
    enterDemoMode,
    exitDemoMode,
    resetDemoData,
    addTask,
    updateTask,
    deleteTask,
    addBooking,
    updateBooking,
    deleteBooking,
    addInvoice,
    updateInvoice,
    deleteInvoice,
    addEstimate,
    updateEstimate,
    deleteEstimate,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addExpense
  ]);

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error("useDemo must be used within a DemoProvider");
  }
  return context;
}

export default DemoContext;
